import _ from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { notificationError } from 'core/utils';
import { BaseViewStore } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { OrderStore, FulfillmentOrderStore } from 'modules/order/stores';
import { FormHelperService } from 'common/data';
import { EditOrderForm } from 'modules/order/forms';
import moment from 'moment';

class EditOrderViewStore extends BaseViewStore {
	@observable stateOptions = [];
	@observable countryOptions = [];
	@observable isCountryLoading = false;
	@observable displayAddressValidation = false;
	@observable useEnteredAddress = true;
	enteredAddress = null;
	updatedAddress = null;
	isAddressVerified = false;
	debouncedCountrySearch = null;
	selectedItem = null;
	isReplacement = false;
	form = new EditOrderForm({
		onSuccess: async () => {
			await this.editOrder();
		},
	});

	constructor(rootStore, { selectedItemId, parentViewStore }) {
		super(rootStore);
		makeObservable(this);
		this.parentView = parentViewStore;
		this.lookupStore = new LookupStore(rootStore);
		this.orderStore = new OrderStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.formHelperService = new FormHelperService();
		this.debouncedCountrySearch = _.debounce((searchPhrase, { action }) => {
			this.searchCountryAsync(searchPhrase, { action });
		}, 500);
		this.initialize(selectedItemId);
	}

	@action.bound
	initialize = async selectedItemId => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');

		await this.getLookupOptions(['country', 'state']);

		if (this.parentView.constructor.name === 'ReplacementViewStore') {
			this.selectedItem = this.parentView.selectedOrder;
			this.isReplacement = true;
		} else {
			await this.getFulfillmentOrder(selectedItemId);
		}

		this.setFormValues();
		this.resume();
	};

	@action.bound
	handleUpdate = async orderDetails => {
		if (this.isReplacement) {
			this.parentView.orderDetails = { ...orderDetails };
			this.parentView.isOrderUpdateRequired = true;
		} else {
			await this.fulfillmentOrderStore.updateFulfillmentOrderInfo(
				orderDetails,
				sourceOfChange.updateFulfillmentOrderInfo
			);
			this.rootStore.notificationStore.success(['FULFILLMENTORDER.EDITMODAL.SUCCESS']);
			this.parentView.reload = true;
			this.parentView.editOrderModal.close();
		}
	};

	@action.bound
	validateAddress = async orderDetails => {
		return this.isReplacement
			? await this.orderStore.validateOrderAddress(orderDetails)
			: await this.fulfillmentOrderStore.validateOrderAddress(orderDetails);
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: [
				'shippingCoreCountryState',
				'shippingCoreCountry',
				'order',
				'order.shippingCoreCountryState',
				'order.shippingCoreCountry',
			],
		});
	};

	@action.bound
	onSelectChange = (value, field) => {
		this.form.$(field.path).set('value', value);
	};

	@action.bound
	editOrder = async (shouldValidate = true) => {
		this.suspend();
		const {
			shippingAddress1,
			shippingAddress2,
			shippingCity,
			shippingZipCode,
			buyerPhone,
			buyerName,
			shippingCoreCountry,
			shippingCoreCountryState,
			shipByDate,
		} = this.form.values();
		let orderDetails = {
			id: this.selectedItem.id,
			orderId: this.selectedItem.orderId,
			shippingAddress1,
			shippingAddress2,
			shippingCity,
			shippingZipCode,
			buyerPhone,
			buyerName,
			shippingCoreCountryId: shippingCoreCountry.value,
			shippingCoreCountryStateId: shippingCoreCountryState.value,
			shipByDate: shipByDate,
			dateFormat: this.dateTimeFormatMonthToUpperCase(this.datePickerFormat.toLowerCase()),
			addressChanged: !this.useEnteredAddress,
			isAddressVerified: this.isAddressVerified,
		};
		try {
			if (shouldValidate) {
				let addressValidationResponse = await this.validateAddress(orderDetails);
				this.isAddressVerified = addressValidationResponse.isAddressVerified;
				orderDetails.isAddressVerified = addressValidationResponse.isAddressVerified;
				orderDetails.addressChanged = addressValidationResponse.addressChanged;

				// If Postgrid updated the address.
				if (addressValidationResponse.enteredAddress != null && addressValidationResponse.updatedAddress != null) {
					this.enteredAddress = addressValidationResponse.enteredAddress;
					this.updatedAddress = addressValidationResponse.updatedAddress;
					this.displayAddressValidation = true;
				}
			}
			if (!this.useEnteredAddress) {
				orderDetails.shippingAddress1 = this.updatedAddress.streetLines[0] ? this.updatedAddress.streetLines[0] : '';
				orderDetails.shippingAddress2 = this.updatedAddress.streetLines[1] ? this.updatedAddress.streetLines[1] : '';
				orderDetails.shippingCity = this.updatedAddress.city;
				orderDetails.shippingZipCode = this.updatedAddress.postalCode;
			}

			if (!this.displayAddressValidation) {
				this.handleUpdate(orderDetails);
				this.resume();
			}
		} catch (err) {
			if (err.statusCode == 400 && err.data == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	dateTimeFormatMonthToUpperCase(string) {
		return string.charAt(0).toUpperCase() + string.charAt(1).toUpperCase() + string.slice(2);
	}

	@action.bound
	setFormValues = () => {
		//If there is any address data info on FO then used those data
		const formItem = this.selectedItem;
		formItem.shipByDate = moment(this.selectedItem.shipByDate)
			.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
			.format(this.datePickerFormat);
		this.formHelperService.updateForm(this.form, formItem, false);
		if (formItem.shippingCoreCountryState) {
			this.form.$('shippingCoreCountryState').set('value', {
				value: formItem.shippingCoreCountryState.id,
				label: formItem.shippingCoreCountryState.name,
			});
		}
		if (formItem.shippingCoreCountry) {
			this.form.$('shippingCoreCountry').set('value', {
				value: formItem.shippingCoreCountry.id,
				label: formItem.shippingCoreCountry.name,
			});
		}
	};

	@action.bound
	getLookupOptions = async lookups => {
		for await (var lookup of lookups) {
			await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName[lookup]);
			this[`${lookup}Options`] = _.map(this.lookupStore.data, item => {
				return { label: item.name, value: item.id, extra: item };
			});
		}
	};

	@action.bound
	async searchCountryAsync(searchPhrase, { action }) {
		if (action === 'input-change') {
			this.isCountryLoading = true;
			this.countryOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.country);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.countryOptions = newOptions;
			this.isCountryLoading = false;
		}
	}

	@action.bound
	async onMenuClose() {
		await this.getLookupOptions(['country']);
	}

	onBack = () => {
		this.isReplacement ? this.parentView.closeModal() : this.parentView.editOrderModal.close();
	};

	@action.bound
	async onValidateAddressBack() {
		this.displayAddressValidation = false;
		this.useEnteredAddress = true;
	}

	@action.bound
	onToggleAddress(useEnteredAddress) {
		this.useEnteredAddress = useEnteredAddress;
	}

	@action.bound
	async onValidateAddressConfirm() {
		this.displayAddressValidation = false;
		await this.editOrder(false);
	}
}

export default EditOrderViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/EditOrderViewStore.js